import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient as defaultClient, type ApiClient, type ProjectSummary } from '../api/client';
import { AppShell } from '../components/AppShell';
import { AuthedBrand } from './AuthedBrand';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Spinner } from '../components/Spinner';
import { InlineAlert } from '../components/InlineAlert';
import { GettingStarted } from '../components/GettingStarted';
import { deriveGettingStarted } from '../components/gettingStartedSteps';
import { useOrg } from '../org/context';
import { roleLabel } from '../org/roleLabels';
import { useFocusMain } from '../a11y/useFocusMain';
import { toDisplayError } from '../api/errorMessages';
import styles from './Home.module.css';

type ProjectsState = 'idle' | 'loading' | 'ready' | 'error';

export interface HomeProps {
  client?: ApiClient;
}

/** Authenticated org landing. Confirms the active organization and its role,
 * lists the org's projects (each links to its journey-health view), and provides
 * sign-out + organization-setup links. */
export function Home({ client = defaultClient }: HomeProps) {
  const { currentOrg } = useOrg();
  useFocusMain();

  const orgId = currentOrg?.id;
  const [phase, setPhase] = useState<ProjectsState>('idle');
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadProjects = useCallback(async () => {
    if (!orgId) return;
    setPhase('loading');
    setLoadError(null);
    try {
      const list = await client.listProjects(orgId);
      setProjects(list);
      setPhase('ready');
    } catch (err) {
      setLoadError(toDisplayError(err).message);
      setPhase('error');
    }
  }, [client, orgId]);

  useEffect(() => {
    if (!orgId) {
      setPhase('idle');
      return;
    }
    void loadProjects();
  }, [orgId, loadProjects]);

  // Getting-started checklist: dismissal is remembered per org (a new org re-shows it).
  const dismissKey = orgId ? `tcj:getting-started-dismissed:${orgId}` : null;
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    setDismissed(dismissKey ? localStorage.getItem(dismissKey) === '1' : false);
  }, [dismissKey]);
  const onDismissGettingStarted = () => {
    if (dismissKey) localStorage.setItem(dismissKey, '1');
    setDismissed(true);
  };
  const onShowGettingStarted = () => {
    if (dismissKey) localStorage.removeItem(dismissKey);
    setDismissed(false);
  };

  const anyVerified = projects.some((p) => p.domainVerificationStatus === 'verified');
  const targetProjectId = anyVerified
    ? (projects.find((p) => p.domainVerificationStatus === 'verified')?.id ?? null)
    : (projects.find((p) => p.domainVerificationStatus !== 'verified')?.id ?? null);
  const gettingStarted = deriveGettingStarted({
    hasProject: projects.length > 0,
    anyVerified,
    anyScanned: projects.some((p) => p.hasCompletedScan),
    anyJourneys: projects.some((p) => p.journeyCount > 0),
    anyTestCases: projects.some((p) => p.testCaseCount > 0),
    targetProjectId,
  });

  const roleText = currentOrg?.role ? roleLabel(currentOrg.role) : 'Member';

  return (
    <AppShell brand={<AuthedBrand />}>
      <Card>
        <h1 className={styles.heading}>{currentOrg?.name ?? 'Your organization'}</h1>
        <p className={styles.role}>
          Your role: <strong>{roleText}</strong>
        </p>
      </Card>

      {orgId && phase === 'ready' ? (
        dismissed ? (
          <Card>
            <div className={styles.showGuide}>
              <span className={styles.showGuideText}>Getting-started guide hidden.</span>
              <Button variant="ghost" onClick={onShowGettingStarted}>
                Show guide
              </Button>
            </div>
          </Card>
        ) : (
          <GettingStarted
            steps={gettingStarted.steps}
            complete={gettingStarted.complete}
            currentStepNumber={gettingStarted.currentStepNumber}
            onDismiss={onDismissGettingStarted}
          />
        )
      ) : null}

      {/* Hidden until the org has a project — a brand-new user creates their first
          project through the Getting Started guide, not an empty table (#472). */}
      {orgId && !(phase === 'ready' && projects.length === 0) ? (
        <Card>
          <div className={styles.projectsHeader}>
            <h2 className={styles.sectionHeading}>Projects</h2>
            <Link className={styles.actionLink} to="/projects/new">
              New project
            </Link>
          </div>
          {phase === 'loading' ? (
            <div className={styles.loading} role="status">
              <Spinner />
              <span>Loading projects…</span>
            </div>
          ) : phase === 'error' ? (
            <div className={styles.errorBlock}>
              <InlineAlert severity="error">{loadError ?? 'Something went wrong.'}</InlineAlert>
              <Button variant="secondary" onClick={() => void loadProjects()}>
                Try again
              </Button>
            </div>
          ) : (
            <ul className={styles.projectList}>
              {projects.map((p) => (
                <li key={p.id}>
                  <Link className={styles.projectLink} to={`/projects/${p.id}/journey-health`}>
                    {p.name}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      ) : null}
    </AppShell>
  );
}
